import React, { useState, useEffect, useCallback, useRef } from "react";
import Meta from "./../components/Meta";
import Section from "./../components/Section";
import Button from "./../components/Button";
import FormAlert from "./../components/FormAlert";
import ReservationForm from "./../components/ReservationForm";
import ConfirmationDialog from "./../components/ConfirmationDialog";
import { requireAdmin } from "./../util/requireAdmin";
import { getReservationsForDate, updateReservation, cancelReservation, getTables, getAvailableTables, getAllAvailableTablesForAdmin, getRecentReservations } from "./../util/db";
import { onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "./../util/firebase";
import { Link } from "react-router-dom";
import { formatPhoneNumber } from "./../util/formatters";

function AdminPage() {
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seatChangeLoading, setSeatChangeLoading] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);
  const [changingSeats, setChangingSeats] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [selectedSeatType, setSelectedSeatType] = useState('table');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  
  // New state for search functionality
  const [searchMode, setSearchMode] = useState('date'); // 'date' or 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [allRecentReservations, setAllRecentReservations] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Use ref to track current reservations to prevent stale closures in event callbacks
  const reservationsRef = useRef(reservations);
  useEffect(() => {
    reservationsRef.current = reservations;
  }, [reservations]);

  // Load tables on component mount
  useEffect(() => {
    const loadTables = async () => {
      const tablesData = await getTables();
      setTables(tablesData);
    };
    loadTables();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    let isMounted = true;
    const loadInitialReservations = async () => {
      setLoading(true);
      try {
        const dateReservations = await getReservationsForDate(selectedDate);
        if (isMounted) {
          setReservations(dateReservations);
        }
      } catch (error) {
        if (isMounted) {
          setFormAlert({
            type: "error",
            message: "Failed to load reservations. Please try again."
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialReservations();

    // Set up real-time listener for reservations
    const reservationsCollection = collection(db, "reservations");
    const q = query(reservationsCollection, where("reservationDate", "==", selectedDate));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isMounted) return;
      
      // Get updated reservations
      const updatedReservations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Only update if there are actual changes
      // Compare by id and fields that would affect rendering
      const needsUpdate = 
        updatedReservations.length !== reservationsRef.current.length ||
        updatedReservations.some((newRes, i) => {
          const oldRes = reservationsRef.current[i];
          if (!oldRes || newRes.id !== oldRes.id) return true;
          return (
            newRes.name !== oldRes.name ||
            newRes.startTime !== oldRes.startTime ||
            newRes.endTime !== oldRes.endTime ||
            newRes.partySize !== oldRes.partySize ||
            JSON.stringify(newRes.tableIds) !== JSON.stringify(oldRes.tableIds) ||
            newRes.seatType !== oldRes.seatType
          );
        });
      
      if (needsUpdate) {
        setReservations(updatedReservations);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [selectedDate]);

  // Handle data loading based on search mode
  useEffect(() => {
    if (searchMode === 'date') {
      // Date mode - handled by existing useEffect above
      return;
    }

    if (searchMode === 'search') {
      // Load recent reservations for search mode
      const loadRecentReservations = async () => {
        setLoading(true);
        try {
          const recentReservations = await getRecentReservations();
          setAllRecentReservations(recentReservations);
          // If there's already a search query, filter the results
          if (searchQuery.trim()) {
            performSearch(searchQuery, recentReservations);
          } else {
            setReservations([]);
          }
        } catch (error) {
          setFormAlert({
            type: "error",
            message: "Failed to load reservations. Please try again."
          });
        } finally {
          setLoading(false);
        }
      };

      loadRecentReservations();
    }
  }, [searchMode]);

  // Perform client-side search filtering
  const performSearch = useCallback((query, recentReservations = allRecentReservations) => {
    if (!query.trim()) {
      setReservations([]);
      return;
    }

    const searchTerm = query.toLowerCase().trim();
    const filteredReservations = recentReservations.filter(reservation => {
      // Check reminderEmail field (partial match, case insensitive)
      const emailMatch = reservation.reminderEmail && reservation.reminderEmail.toLowerCase().includes(searchTerm);
      
      // Check name field (contains match, case insensitive)
      const nameMatch = reservation.name && reservation.name.toLowerCase().includes(searchTerm);
      
      return emailMatch || nameMatch;
    });

    // Sort by reverse chronological order (most recent first)
    const sortedReservations = filteredReservations.sort((a, b) => {
      // First sort by reservation date (descending - most recent dates first)
      if (a.reservationDate !== b.reservationDate) {
        return b.reservationDate.localeCompare(a.reservationDate);
      }
      // If same date, sort by start time (descending - latest times first)
      return b.startTime - a.startTime;
    });

    setReservations(sortedReservations);
  }, [allRecentReservations]);

  // Handle search query changes with debouncing
  const handleSearchChange = useCallback((newQuery) => {
    setSearchQuery(newQuery);
    
    if (searchMode !== 'search') return;
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      performSearch(newQuery);
    }, 300); // 300ms debounce
    
    setSearchTimeout(newTimeout);
  }, [searchMode, searchTimeout, performSearch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const loadReservations = useCallback(async (date) => {
    if (!date) return;
    
    setLoading(true);
    try {
      const dateReservations = await getReservationsForDate(date);
      setReservations(dateReservations);
    } catch (error) {
      setFormAlert({
        type: "error",
        message: "Failed to load reservations. Please try again."
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCancel = useCallback(async (reservationId) => {
    setReservationToCancel(reservationId);
    setShowCancelDialog(true);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    try {
      await cancelReservation(reservationToCancel);
      
      // Immediately update local state to remove the canceled reservation (optimistic update)
      setReservations(prevReservations => 
        prevReservations.filter(res => res.id !== reservationToCancel)
      );
      
      setFormAlert({
        type: "success",
        message: "Reservation cancelled successfully."
      });
      
      // Refresh data based on current mode
      if (searchMode === 'date' && selectedDate) {
        // For date mode, reload reservations for the selected date
        // The real-time listener should also update, but this ensures immediate update
        loadReservations(selectedDate);
      } else if (searchMode === 'search' && searchQuery.trim()) {
        // For search mode, re-run the search to get updated results
        const recentReservations = await getRecentReservations();
        setAllRecentReservations(recentReservations);
        performSearch(searchQuery, recentReservations);
      }
    } catch (error) {
      setFormAlert({
        type: "error",
        message: "Failed to cancel reservation. Please try again."
      });
    } finally {
      setShowCancelDialog(false);
      setReservationToCancel(null);
    }
  }, [loadReservations, selectedDate, reservationToCancel, searchMode, searchQuery, performSearch]);

  const handleEdit = useCallback((reservation) => {
    setEditingReservation(reservation);
  }, []);

  const handleUpdateReservation = async (data) => {
    setFormAlert({ type: "info", message: "Updating reservation..." });

    try {
      // Check if data.time is missing or null
      if (!data.time) {
        console.error('Time is missing in form submission', data);
        throw new Error("No time selected for the reservation");
      }
      
      // Convert selected time to military format (e.g., "19:00" to 1900)
      const [hours, minutes] = data.time.split(":");
      const startTime = parseInt(hours + minutes);
      
      // Calculate end time properly handling minutes (90-minute reservation)
      const totalMinutes = (hours * 60 + parseInt(minutes)) + 90;
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      const endTime = (endHours * 100) + endMinutes;

      // Create update object with only the fields that have changed
      const updateData = {};
      
      // Always update party size if it's different
      const parsedPartySize = parseInt(data.partySize);
      if (!isNaN(parsedPartySize) && parsedPartySize !== editingReservation.partySize) {
        updateData.partySize = parsedPartySize;
      }

      // Only update time-related fields if they've changed
      if (startTime !== editingReservation.startTime) {
        updateData.startTime = startTime;
        updateData.endTime = endTime;
      }
      if (data.date !== editingReservation.reservationDate) {
        updateData.reservationDate = data.date;
      }
      if (data.name !== editingReservation.name) {
        updateData.name = data.name;
      }
      if (data.phoneNumber !== editingReservation.phoneNumber) {
        updateData.phoneNumber = data.phoneNumber;
      }
      if (data.reminderEmail !== editingReservation.reminderEmail) {
        updateData.reminderEmail = data.reminderEmail;
      }
      
      // Check if seat type has changed
      const seatTypeChanged = data.seatType !== editingReservation.seatType;
      if (seatTypeChanged) {
        updateData.seatType = data.seatType || 'table';
      }

      // Check if tables were manually selected by the admin
      const hasManualTableSelection = data.tableIds && Array.isArray(data.tableIds) && data.tableIds.length > 0;
      
      if (hasManualTableSelection) {
        // Admin has manually selected tables - use those instead of automatic assignment
        updateData.tableIds = data.tableIds;
      } else {
        // Only check for available tables and update tableIds if:
        // 1. The time has changed, OR
        // 2. The date has changed, OR
        // 3. The party size has changed, OR
        // 4. The seat type has changed
        // AND no manual table selection was made
        if (updateData.startTime || updateData.reservationDate || updateData.partySize || updateData.seatType) {
          // For table assignment in the update flow, we need to pick optimal tables
          // Get all available tables first
          const allAvailableTables = await getAllAvailableTablesForAdmin(
            updateData.reservationDate || editingReservation.reservationDate,
            updateData.startTime || editingReservation.startTime,
            updateData.endTime || editingReservation.endTime,
            editingReservation.id,
            updateData.seatType || editingReservation.seatType || 'table'
          );

          if (allAvailableTables.length === 0) {
            throw new Error("That time slot has been booked. Please refresh the page.");
          }

          // For counter seats, take multiple seats if needed
          if ((updateData.seatType || editingReservation.seatType) === 'counter') {
            // Sort counter seats by ID to get consecutive ones if possible
            const sortedCounterSeats = [...allAvailableTables].sort((a, b) => {
              const aNum = parseInt(a.id.substring(1));
              const bNum = parseInt(b.id.substring(1));
              return aNum - bNum;
            });
            
            // Get the correct number of counter seats
            const neededSeats = updateData.partySize || editingReservation.partySize;
            
            if (sortedCounterSeats.length < neededSeats) {
              throw new Error(`Not enough counter seats available. Found ${sortedCounterSeats.length}, need ${neededSeats}.`);
            }
            
            // Try to find consecutive seats
            let bestConsecutiveStart = 0;
            let bestConsecutiveLength = 0;
            let currentConsecutiveStart = 0;
            let currentConsecutiveLength = 1;
            
            for (let i = 1; i < sortedCounterSeats.length; i++) {
              const prevNum = parseInt(sortedCounterSeats[i-1].id.substring(1));
              const currNum = parseInt(sortedCounterSeats[i].id.substring(1));
              
              if (currNum === prevNum + 1) {
                // Continue consecutive sequence
                currentConsecutiveLength++;
              } else {
                // Sequence broken, check if previous sequence was better
                if (currentConsecutiveLength > bestConsecutiveLength) {
                  bestConsecutiveLength = currentConsecutiveLength;
                  bestConsecutiveStart = currentConsecutiveStart;
                }
                // Start new sequence
                currentConsecutiveStart = i;
                currentConsecutiveLength = 1;
              }
            }
            
            // Check final sequence
            if (currentConsecutiveLength > bestConsecutiveLength) {
              bestConsecutiveLength = currentConsecutiveLength;
              bestConsecutiveStart = currentConsecutiveStart;
            }
            
            // Use consecutive seats if we have enough, otherwise use any seats
            if (bestConsecutiveLength >= neededSeats) {
              updateData.tableIds = sortedCounterSeats
                .slice(bestConsecutiveStart, bestConsecutiveStart + neededSeats)
                .map(table => table.id);
            } else {
              updateData.tableIds = sortedCounterSeats.slice(0, neededSeats).map(table => table.id);
            }
          } else {
            // For tables, try to find the best combination
            
            // First try to find a single table with enough seats
            const singleTableWithCapacity = allAvailableTables.find(table => 
              table.numSeats >= (updateData.partySize || editingReservation.partySize)
            );
            
            if (singleTableWithCapacity) {
              updateData.tableIds = [singleTableWithCapacity.id];
            } else {
              // No single table with enough capacity, sort tables by ID
              const sortedTables = [...allAvailableTables].sort((a, b) => {
                const aMatch = a.id.match(/(\d+)/);
                const bMatch = b.id.match(/(\d+)/);
                const aNum = aMatch ? parseInt(aMatch[0]) : 0;
                const bNum = bMatch ? parseInt(bMatch[0]) : 0;
                return aNum - bNum;
              });
              
              // Group tables into consecutive sequences
              const consecutiveGroups = [];
              let currentGroup = [];
              
              for (let i = 0; i < sortedTables.length; i++) {
                const currentTable = sortedTables[i];
                
                if (currentGroup.length === 0) {
                  currentGroup.push(currentTable);
                  continue;
                }
                
                const currentMatch = currentTable.id.match(/(\d+)/);
                const previousMatch = currentGroup[currentGroup.length - 1].id.match(/(\d+)/);
                
                const currentNum = currentMatch ? parseInt(currentMatch[0]) : 0;
                const previousNum = previousMatch ? parseInt(previousMatch[0]) : 0;
                
                if (currentNum === previousNum + 1) {
                  currentGroup.push(currentTable);
                } else {
                  if (currentGroup.length > 0) {
                    consecutiveGroups.push([...currentGroup]);
                  }
                  currentGroup = [currentTable];
                }
              }
              
              if (currentGroup.length > 0) {
                consecutiveGroups.push([...currentGroup]);
              }
              
              // Find best subsequence of consecutive tables with the closest fit
              let bestTables = null;
              let bestTotalSeats = Infinity;
              const requiredSeats = updateData.partySize || editingReservation.partySize;
              
              // For each consecutive group, find the best subsequence
              for (const group of consecutiveGroups) {
                // For each starting position in the group
                for (let start = 0; start < group.length; start++) {
                  // Try subsequences of different lengths
                  for (let len = 1; len <= group.length - start; len++) {
                    const subgroup = group.slice(start, start + len);
                    const totalSeats = subgroup.reduce((sum, table) => sum + table.numSeats, 0);
                    
                    // If this subsequence has enough seats
                    if (totalSeats >= requiredSeats) {
                      // If this is a better fit than what we've found so far
                      if (totalSeats < bestTotalSeats) {
                        bestTotalSeats = totalSeats;
                        bestTables = subgroup;
                      }
                    }
                  }
                }
              }
              
              if (bestTables) {
                updateData.tableIds = bestTables.map(table => table.id);
              } else {
                throw new Error("Couldn't find a suitable combination of tables for this party size");
              }
            }
          }
        }
      }

      await updateReservation(editingReservation.id, updateData, true);

      // Update the local reservations state to reflect the changes
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === editingReservation.id 
            ? { ...res, ...updateData } 
            : res
        )
      );

      setFormAlert({ type: "success", message: "Reservation updated successfully!" });
      setEditingReservation(null);
    } catch (error) {
      setFormAlert({ 
        type: "error", 
        message: error.message || "Failed to update reservation. Please try again." 
      });
    }
  };

  // Format time from military format (e.g., 1900) to 12-hour format (e.g., 7:00 PM)
  const formatTime = (militaryTime) => {
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Format date to a readable string without timezone conversion
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get reservations for a specific table
  const getTableReservations = (tableId) => {
    return reservations.filter(r => r.tableIds?.includes(tableId));
  };

  // Filter tables by type
  const floorTables = tables.filter(table => table.location === 'floor');
  const counterTables = tables
    .filter(table => table.location === 'counter')
    .sort((a, b) => {
      // Extract the number from the table ID (e.g., "C1" -> 1)
      const numA = parseInt(a.id.replace('C', ''));
      const numB = parseInt(b.id.replace('C', ''));
      return numA - numB;
    });

  const handleChangeSeats = useCallback(async (reservation, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setChangingSeats(reservation);
    setSelectedSeatType(reservation.seatType || 'table');
    setSelectedTableIds(reservation.tableIds || []);
    setSeatChangeLoading(true);
    try {
      // Get available tables for the reservation's time slot
      const [hours, minutes] = [
        Math.floor(reservation.startTime / 100),
        reservation.startTime % 100
      ];
      const startTime = hours * 100 + minutes;
      const endTime = reservation.endTime;

      const available = await getAllAvailableTablesForAdmin(
        reservation.reservationDate,
        startTime,
        endTime,
        reservation.id,
        reservation.seatType || 'table'
      );
      setAvailableTables(available);
    } catch (error) {
      setFormAlert({
        type: "error",
        message: "Failed to load available tables. Please try again."
      });
    } finally {
      setSeatChangeLoading(false);
    }
  }, []);

  const handleSeatTypeChange = useCallback(async (newSeatType) => {
    if (!changingSeats) return;
    
    setSelectedSeatType(newSeatType);
    setSelectedTableIds([]); // Clear selected tables when changing seat type
    setSeatChangeLoading(true);
    try {
      const [hours, minutes] = [
        Math.floor(changingSeats.startTime / 100),
        changingSeats.startTime % 100
      ];
      const startTime = hours * 100 + minutes;
      const endTime = changingSeats.endTime;

      const available = await getAllAvailableTablesForAdmin(
        changingSeats.reservationDate,
        startTime,
        endTime,
        changingSeats.id,
        newSeatType
      );
      setAvailableTables(available);
    } catch (error) {
      setFormAlert({
        type: "error",
        message: "Failed to load available tables. Please try again."
      });
    } finally {
      setSeatChangeLoading(false);
    }
  }, [changingSeats]);

  const toggleTableSelection = useCallback((tableId) => {
    setSelectedTableIds(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  }, []);

  const resetSeatChange = useCallback(() => {
    setChangingSeats(null);
    setAvailableTables([]);
    setSelectedTableIds([]);
    setSelectedSeatType('table');
    // Clear error messages when canceling seat changes
    if (formAlert && formAlert.type === "error") {
      setFormAlert(null);
    }
  }, [formAlert]);

  const handleSeatSelection = useCallback(async () => {
    if (!changingSeats) return;

    try {
      // Calculate total capacity of selected tables
      const selectedTables = tables.filter(table => selectedTableIds.includes(table.id));
      const totalCapacity = selectedTables.reduce((sum, table) => sum + table.numSeats, 0);
      
      // Check if selected tables can accommodate the party size
      if (totalCapacity < changingSeats.partySize) {
        setFormAlert({
          type: "error",
          message: `Selected seats can only accommodate ${totalCapacity} people, but this reservation is for ${changingSeats.partySize} people.`
        });
        return;
      }

      // For counter seats, check that we have exactly the right number of seats
      if (selectedSeatType === 'counter' && totalCapacity > changingSeats.partySize) {
        // Just a warning for counter seats, not a blocker
        setFormAlert({
          type: "warning",
          message: `You've selected ${totalCapacity} counter seats for a party of ${changingSeats.partySize}. Consider using exactly ${changingSeats.partySize} seats to avoid wasting capacity.`
        });
        // Continue with the update despite the warning
      }

      await updateReservation(changingSeats.id, {
        tableIds: selectedTableIds,
        seatType: selectedSeatType
      }, true);
      
      // Update the local reservations state to reflect the changes
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === changingSeats.id 
            ? { 
                ...res, 
                tableIds: selectedTableIds, 
                seatType: selectedSeatType 
              } 
            : res
        )
      );
      
      // If this is the same reservation being edited in the form, update that too
      if (editingReservation && editingReservation.id === changingSeats.id) {
        setEditingReservation(prev => ({
          ...prev,
          tableIds: selectedTableIds,
          seatType: selectedSeatType
        }));
      }
      
      setFormAlert({
        type: "success",
        message: "Seats updated successfully!"
      });
      resetSeatChange();
    } catch (error) {
      setFormAlert({
        type: "error",
        message: "Failed to update seats. Please try again."
      });
    }
  }, [changingSeats, selectedTableIds, selectedSeatType, resetSeatChange, editingReservation, tables, availableTables]);

  const handlePrintReservations = useCallback(() => {
    if (reservations.length === 0) {
      setFormAlert({
        type: "warning",
        message: "No reservations to print. Please select a date or search for reservations first."
      });
      return;
    }

    // Create print window
    const printWindow = window.open('', '_blank');
    
    const sortedReservations = [...reservations].sort((a, b) => {
      // First sort by start time
      if (a.startTime !== b.startTime) {
        return a.startTime - b.startTime;
      }
      // Then sort by the first table ID if available
      const aFirstTable = a.tableIds && a.tableIds.length > 0 ? a.tableIds[0] : "";
      const bFirstTable = b.tableIds && b.tableIds.length > 0 ? b.tableIds[0] : "";
      return aFirstTable.localeCompare(bFirstTable);
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Reservations - ${searchMode === 'date' && selectedDate ? formatDate(selectedDate) : 'Search Results'}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              line-height: 1.3;
              color: #000;
              background: white;
            }
            
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
            }
            
            .header h1 {
              font-size: 20px;
              margin-bottom: 5px;
            }
            
            .header .subtitle {
              font-size: 14px;
              color: #666;
            }
            
            .summary {
              margin-bottom: 20px;
              text-align: center;
              font-weight: bold;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            
            th, td {
              border: 1px solid #000;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
            }
            
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              font-size: 11px;
            }
            
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            
            .time-cell {
              white-space: nowrap;
              font-weight: bold;
            }
            
            .party-size {
              text-align: center;
              font-weight: bold;
            }
            
            .tables-cell {
              font-size: 11px;
            }
            
            .contact-info {
              font-size: 10px;
            }
            
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .header {
                page-break-after: avoid;
              }
              
              tr {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Reservations Report</h1>
            <div class="subtitle">
              ${searchMode === 'date' && selectedDate 
                ? `Date: ${formatDate(selectedDate)}` 
                : `Search Results for: "${searchQuery}"`}
            </div>
            <div class="subtitle">
              Generated on: ${new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
          
          <div class="summary">
            Total Reservations: ${sortedReservations.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Party</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Tables</th>
                ${searchMode === 'search' ? '<th>Date</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${sortedReservations.map(reservation => `
                <tr>
                  <td class="time-cell">
                    ${formatTime(reservation.startTime)}<br>
                    <span style="font-size: 10px; color: #666;">to ${formatTime(reservation.endTime)}</span>
                  </td>
                  <td class="party-size">${reservation.partySize}</td>
                  <td>${reservation.name || 'N/A'}</td>
                  <td class="contact-info">${formatPhoneNumber(reservation.phoneNumber) || 'N/A'}</td>
                  <td class="contact-info">${reservation.reminderEmail || reservation.email || 'N/A'}</td>
                  <td class="tables-cell">
                    ${reservation.tableIds && reservation.tableIds.length > 0 
                      ? reservation.tableIds.join(', ') 
                      : 'Unassigned'}
                    ${reservation.seatType === 'counter' ? '<br><small>(Counter)</small>' : ''}
                  </td>
                  ${searchMode === 'search' ? `<td class="contact-info">${formatDate(reservation.reservationDate)}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>SMS Sushi Reservations System</p>
            <p>This report contains confidential customer information. Please handle with care.</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }, [reservations, searchMode, selectedDate, searchQuery, formatDate, formatTime]);

  return (
    <>
      <Meta title="Admin Dashboard" />
      <Section
        size="md"
        bgColor="bg-white"
        bgImage=""
        bgImageOpacity={1}
        textColor=""
      >
        <div className="container mx-auto px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Admin Dashboard</h1>
          
          <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md border border-green-200">
            <p className="text-sm font-medium">Admin mode: You can book non-reservable tables and counter seats. These will automatically be included in the available options.</p>
          </div>
          
          {formAlert && (
            <div className="max-w-2xl mx-auto mb-4 sm:mb-6">
              <FormAlert type={formAlert.type} message={formAlert.message} />
            </div>
          )}

          <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
            {/* Search Mode Toggle */}
            <div className="mb-4">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    setSearchMode('date');
                    setSearchQuery('');
                    setReservations([]); // Clear search results
                    setAllRecentReservations([]); // Clear cached recent reservations
                    // Clear any existing search results
                    if (editingReservation) {
                      setEditingReservation(null);
                    }
                    if (changingSeats) {
                      resetSeatChange();
                    }
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    searchMode === 'date'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Search by Date
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchMode('search');
                    setSelectedDate(null);
                    setReservations([]); // Clear date-based results
                    // Clear any existing date-based results
                    if (editingReservation) {
                      setEditingReservation(null);
                    }
                    if (changingSeats) {
                      resetSeatChange();
                    }
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    searchMode === 'search'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Search by Name/Email
                </button>
              </div>
            </div>

            {/* Search Inputs */}
            <div className="space-y-4">
              {searchMode === 'date' ? (
                <div>
                  <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="topDateSelector">
                    Select Date
                  </label>
                  <input
                    type="date"
                    id="topDateSelector"
                    name="topDateSelector"
                    className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base"
                    onChange={(e) => {
                      const date = e.target.value;
                      if (editingReservation) {
                        // Cancel any ongoing edits to prevent async issues
                        setEditingReservation(null);
                      }
                      if (changingSeats) {
                        // Cancel any ongoing seat changes to prevent async issues
                        resetSeatChange();
                      }
                      setSelectedDate(date);
                    }}
                    value={selectedDate || ""}
                    lang="en-US"
                  />
                </div>
              ) : (
                <div>
                  <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="searchQuery">
                    Search by Name or Email
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="searchQuery"
                      name="searchQuery"
                      placeholder="Enter customer name or email address..."
                      className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base pr-10"
                      value={searchQuery}
                      onChange={(e) => {
                        const newQuery = e.target.value;
                        handleSearchChange(newQuery);
                        if (editingReservation) {
                          setEditingReservation(null);
                        }
                        if (changingSeats) {
                          resetSeatChange();
                        }
                      }}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  {searchQuery && (
                    <p className="mt-1 text-xs text-gray-500">
                      Press Enter to search or continue typing to refine your search
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Print Button - Show when there are reservations to print */}
          {((searchMode === 'date' && selectedDate) || (searchMode === 'search' && searchQuery.trim())) && reservations.length > 0 && (
            <div className="max-w-2xl mx-auto mb-6">
              <div className="flex justify-center">
                <Button
                  onClick={handlePrintReservations}
                  variant="secondary"
                  className="flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Print Reservations ({reservations.length})</span>
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (searchMode === 'date' && selectedDate && tables.length > 0) || (searchMode === 'search' && searchQuery.trim()) ? (
            <>
              {/* Only show table layout for date-based searches */}
              {searchMode === 'date' && (
                <div className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold mb-4">Table Layout</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                    {/* Floor Tables */}
                    <div>
                      <h3 className="text-lg font-medium mb-3">Floor Tables</h3>
                      <div className="space-y-3 sm:space-y-4">
                        {floorTables.map((table) => {
                          const tableReservations = getTableReservations(table.id);
                          return (
                            <div key={table.id} className="bg-white rounded-lg shadow-md p-3 sm:p-4">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                                <div>
                                  <h4 className="font-medium text-sm sm:text-base">Table {table.id}</h4>
                                  <p className="text-xs sm:text-sm text-gray-600">{table.numSeats} seats</p>
                                </div>
                                <div className="space-y-1">
                                  {tableReservations.map((reservation) => (
                                    <div
                                      key={reservation.id}
                                      className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                                    >
                                      {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Counter Tables */}
                    <div>
                      <h3 className="text-lg font-medium mb-3">Counter Tables</h3>
                      <div className="space-y-3 sm:space-y-4">
                        {counterTables.map((table) => {
                          const tableReservations = getTableReservations(table.id);
                          return (
                            <div key={table.id} className="bg-white rounded-lg shadow-md p-3 sm:p-4">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                                <div>
                                  <h4 className="font-medium text-sm sm:text-base">Counter {table.id}</h4>
                                  <p className="text-xs sm:text-sm text-gray-600">{table.numSeats} seats</p>
                                </div>
                                <div className="space-y-1">
                                  {tableReservations.map((reservation) => (
                                    <div
                                      key={reservation.id}
                                      className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                                    >
                                      {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* All Reservations List */}
              <div className="mt-10">
                <h2 className="text-xl sm:text-2xl font-semibold mb-4">
                  {searchMode === 'date' ? 'All Reservations' : 'Search Results'}
                </h2>
                {searchMode === 'search' && searchQuery.trim() && (
                  <div className="mb-4 text-sm text-gray-600">
                    Searching for: "<span className="font-medium">{searchQuery}</span>"
                    {reservations.length > 0 && (
                      <span className="ml-2">- Found {reservations.length} result{reservations.length === 1 ? '' : 's'}</span>
                    )}
                  </div>
                )}
                <div className="max-w-4xl mx-auto space-y-4">
                  {reservations.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 text-center">
                      <p className="text-gray-600">
                        {searchMode === 'date' 
                          ? 'No reservations found for this date.' 
                          : 'No reservations found matching your search.'}
                      </p>
                    </div>
                  ) : (
                    [...reservations]
                      .sort((a, b) => {
                        // First sort by reservation date (descending - furthest in future first)
                        if (a.reservationDate !== b.reservationDate) {
                          return b.reservationDate.localeCompare(a.reservationDate);
                        }
                        // If same date, sort by start time (ascending - earliest times first)
                        if (a.startTime !== b.startTime) {
                          return a.startTime - b.startTime;
                        }
                        // Then sort by the first table ID if available
                        const aFirstTable = a.tableIds && a.tableIds.length > 0 ? a.tableIds[0] : "";
                        const bFirstTable = b.tableIds && b.tableIds.length > 0 ? b.tableIds[0] : "";
                        return aFirstTable.localeCompare(bFirstTable);
                      })
                      .map((reservation) => (
                      <div key={reservation.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                        {editingReservation?.id === reservation.id ? (
                          <ReservationForm
                            onSubmit={handleUpdateReservation}
                            initialValues={{
                              date: reservation.reservationDate,
                              partySize: reservation.partySize,
                              time: `${Math.floor(reservation.startTime / 100).toString().padStart(2, '0')}:${(reservation.startTime % 100).toString().padStart(2, '0')}`,
                              name: reservation.name,
                              phoneNumber: reservation.phoneNumber,
                              seatType: reservation.seatType || 'table',
                              reminderEmail: reservation.reminderEmail
                            }}
                            submitButtonText="Save Changes"
                            excludeReservationId={reservation.id}
                            onCancel={() => setEditingReservation(null)}
                            showCancelButton={true}
                            cancelButtonText="Cancel"
                            isAdmin={true}
                          />
                        ) : (
                          <>
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
                              <div>
                                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                                  {formatDate(reservation.reservationDate)}
                                </h3>
                                <p className="text-sm sm:text-base text-gray-600">
                                  {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                                </p>
                                <p className="text-sm sm:text-base text-gray-600">
                                  {reservation.partySize} {reservation.partySize === 1 ? 'person' : 'people'}
                                </p>
                                <p className="text-sm sm:text-base text-gray-600">
                                  <strong>Email:</strong> {reservation.email}
                                </p>
                                <p className="text-sm sm:text-base text-gray-600">
                                  <strong>Name:</strong> {reservation.name}
                                </p>
                                <p className="text-sm sm:text-base text-gray-600">
                                  <strong>Phone:</strong> {reservation.phoneNumber}
                                </p>
                                <div className="flex flex-wrap items-start space-x-2">
                                  <span className="font-medium">Tables:</span>
                                  <span>
                                    {reservation.tableIds && reservation.tableIds.length > 0 
                                      ? (
                                          <>
                                            {reservation.tableIds.join(', ')}
                                            {reservation.tableIds.length > 1 && reservation.seatType === 'table' && (
                                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
                                                Multiple Tables
                                              </span>
                                            )}
                                          </>
                                        )
                                      : 'No tables assigned'
                                    }
                                  </span>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => handleEdit(reservation)}
                                  size="sm"
                                >
                                  Edit
                                </Button>
                                <Button
                                  onClick={(e) => handleChangeSeats(reservation, e)}
                                  size="sm"
                                  variant="secondary"
                                >
                                  Change Seats
                                </Button>
                                <Button
                                  onClick={() => handleCancel(reservation.id)}
                                  size="sm"
                                  variant="danger"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>

                            {changingSeats?.id === reservation.id && (
                              <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-lg font-semibold mb-4">Change Seats</h3>
                                <p className="text-sm text-gray-600 mb-2">
                                  Current tables: {changingSeats.tableIds?.join(', ') || 'No tables assigned'}
                                </p>
                                <p className="text-sm text-gray-600 mb-4">
                                  Party size: <span className="font-medium">{changingSeats.partySize} {changingSeats.partySize === 1 ? 'person' : 'people'}</span>
                                </p>
                                
                                <div className="mb-4">
                                  <label className="block mb-2 font-medium text-sm">Seat Type</label>
                                  <div className="flex space-x-4">
                                    <button
                                      type="button"
                                      onClick={() => handleSeatTypeChange('table')}
                                      className={`px-4 py-2 rounded border ${
                                        selectedSeatType === 'table'
                                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                                          : 'border-gray-200 hover:border-blue-500'
                                      }`}
                                    >
                                      Table
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleSeatTypeChange('counter')}
                                      className={`px-4 py-2 rounded border ${
                                        selectedSeatType === 'counter'
                                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                                          : 'border-gray-200 hover:border-blue-500'
                                      }`}
                                    >
                                      Counter
                                    </button>
                                  </div>
                                </div>

                                {seatChangeLoading ? (
                                  <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                  </div>
                                ) : (
                                  <div className="space-y-4">
                                    {/* Selected tables capacity info */}
                                    {selectedTableIds.length > 0 && (
                                      <div className="bg-blue-50 rounded-md p-3 border border-blue-200">
                                        <div className="flex justify-between items-center">
                                          <span className="font-medium text-blue-800">Selected tables: {selectedTableIds.join(', ')}</span>
                                          <span className="text-sm px-2 py-1 bg-blue-100 rounded-full text-blue-800">
                                            {(() => {
                                              const selectedTables = tables.filter(table => selectedTableIds.includes(table.id));
                                              const totalCapacity = selectedTables.reduce((sum, table) => sum + table.numSeats, 0);
                                              return `Capacity: ${totalCapacity}/${changingSeats.partySize}`;
                                            })()}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {availableTables.length === 0 ? (
                                      <div className="text-center text-gray-500 py-4">
                                        No available {selectedSeatType === 'table' ? 'tables' : 'counter seats'} found for this time slot.
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {availableTables.map((table) => (
                                          <button
                                            key={table.id}
                                            type="button"
                                            onClick={() => toggleTableSelection(table.id)}
                                            className={`p-3 rounded border text-left ${
                                              selectedTableIds.includes(table.id)
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300'
                                            }`}
                                          >
                                            <div className="font-medium">{table.id}</div>
                                            <div className="text-sm text-gray-600">{table.numSeats} {table.numSeats === 1 ? 'seat' : 'seats'}</div>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    
                                    <div className="flex justify-end space-x-2 mt-4">
                                      <Button
                                        onClick={resetSeatChange}
                                        size="sm"
                                        variant="secondary"
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        onClick={handleSeatSelection}
                                        size="sm"
                                        disabled={selectedTableIds.length === 0}
                                      >
                                        Save Changes
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-4 sm:p-6 text-center">
              <p className="text-gray-600">
                {searchMode === 'date' 
                  ? 'Please select a date to view table reservations.' 
                  : 'Enter a name or email address to search for reservations.'}
              </p>
            </div>
          )}
          
          {/* Privacy Policy Link */}
          <div className="mt-10 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              We only use customer email addresses to send reservation reminders on the morning of their reservation.
              We do not use customer data for any other purposes. See our{" "}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>{" "}
              for more information.
            </p>
          </div>
        </div>
      </Section>

      <ConfirmationDialog
        isOpen={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setReservationToCancel(null);
        }}
        onConfirm={handleCancelConfirm}
        title="Cancel Reservation"
        message="Are you sure you want to cancel this reservation?"
      />
    </>
  );
}

// Wrap component with requireAdmin higher-order component
export default requireAdmin(AdminPage); 